import React from 'react'
import { useForm } from '@inertiajs/react'
import { Survey, SurveySet } from '@/types'
import NPSQuestion from '@/components/questions/NPSQuestion'
import MultipleOptionQuestion from '@/components/questions/MultipleOptionQuestion'
import OpenEndedQuestion from '@/components/questions/OpenEndedQuestion'

interface QuestionsScreenProps {
  survey: Survey
  surveySet: SurveySet
  initialData: any // The data from the parent component
  onDataChange: (data: any) => void
}

const QuestionsScreen: React.FC<QuestionsScreenProps> = ({ survey, surveySet, initialData, onDataChange }) => {
  const { data, setData } = useForm(initialData)

  const handleAnswerChange = (questionId: number, content: string) => {
    const updatedAnswers = data.answer.answer_questions_attributes.map((answer: { question_id: number; content: string }) => {
      if (answer.question_id === questionId) {
        return { ...answer, content }
      }
      return answer
    })

    const newData = {
      ...data,
      answer: {
        ...data.answer,
        answer_questions_attributes: updatedAnswers
      }
    }
    setData(newData)
    onDataChange(newData)
  }

  const totalQuestions = survey.questions.length
  const optionalQuestions = survey.questions.filter(q => !q.required).length

  const questionsText = `Son ${totalQuestions} pregunta${totalQuestions > 1 ? 's' : ''}`
  const optionalText =
    optionalQuestions > 0 ? ` y ${optionalQuestions} ${optionalQuestions > 1 ? 'son opcionales' : 'es opcional'}` : ''

  const timeComponent = () => {
    return (
      <div className="border border-purple-300 rounded-md px-3 py-1 text-sm text-purple-700 flex items-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>Menos de {survey.minutes || 1} min</span>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1
              className="text-3xl font-bold my-2 w-fit"
            >
              {survey.translated_name}
            </h1>
            <div className="max-w-42 md:hidden">{timeComponent()}</div>
            <p
              className="text-gray-800 text-lg my-2 w-fit"
            >
              <span className="font-semibold">
                [{surveySet.measurement_type === 'digital_service' ? 'Servicio' : 'Plataforma'}]:
              </span>{' '}
              {surveySet.service_name}
            </p>
            <p
              className="text-gray-800 mt-4 w-fit"
            >
              {survey.description} {`${questionsText}${optionalText}.`}
            </p>
          </div>
          <div className="flex-shrink-0 hidden md:block">{timeComponent()}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 ">
        {survey.questions
          .sort((a, b) => a.position - b.position)
          .map((question, index) => {
            const isAccessibility = survey.name === 'accessibility'
            const questionBody = `${!isAccessibility ? `${index + 1}. ` : ''}${question.body}${
              !question.required ? ' (Opcional)' : ''
            }`

            return (
              <div key={question.id} className="  p-6 mb-6">
                {isAccessibility && (
                  <h2 className="font-semibold  mb-4">{`Pregunta ${index + 1} de ${survey.questions.length}`}</h2>
                )}

                {question.kind === 'nps' && (
                  <NPSQuestion
                    body={questionBody}
                    required={question.required}
                    readOnly={false}
                    onChange={content => handleAnswerChange(question.id, content)}
                  />
                )}
                {question.kind === 'multiple_option' && (
                  <MultipleOptionQuestion
                    questionId={question.id}
                    body={questionBody}
                    required={question.required}
                    options={question.payload?.options || []}
                    readOnly={true} // Corregido: modo de visualización para responder
                    onUpdateOption={(_, value) => handleAnswerChange(question.id, value)}
                  />
                )}
                {question.kind === 'open_ended' && (
                  <OpenEndedQuestion
                    body={questionBody}
                    required={question.required}
                    questionId={question.id}
                    readOnly={false}
                    onChange={content => handleAnswerChange(question.id, content)}
                  />
                )}
              </div>
            )
          })}
      </div>
    </div>
  )
}

export default QuestionsScreen
